import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ApiSuccess, Branding, FeatureKey, ResolvedBranding, Terminology } from '@lkq/shared';
import { FALLBACK_BRANDING, resolveEffectiveFeatures } from '@lkq/shared';
import { api } from '@/services/api';
import { applyBranding } from '@/lib/apply-branding';
import { readCachedBranding, writeCachedBranding } from '@/lib/branding-cache';

// Branding resolution + live preview (docs/09): theme, terminology and
// feature flags arrive in one concept-config response keyed by hostname.

export interface ConceptConfig {
  concept: { id: string; name: string; slug: string; domain: string; supportEmail: string } | null;
  branding: ResolvedBranding;
  terminology: Terminology;
  features: Record<FeatureKey, boolean>;
}

interface ThemeContextValue {
  config: ConceptConfig;
  /** The branding actually painted right now: draft preview ?? portal skin ?? host config. */
  branding: ResolvedBranding;
  isLoading: boolean;
  /** Terminology override → i18n default fallback (docs/09). */
  term: (key: string, fallback: string) => string;
  features: Record<FeatureKey, boolean>;
  setPreview: (branding: Partial<Branding>, logoUrl?: string | null) => void;
  clearPreview: () => void;
  /** Persistent skin layer for the Concept Management portal (docs/03) — sits under draft previews. */
  setPortalSkin: (branding: ResolvedBranding | null) => void;
  /** Active-concept terminology + features for the Concept Management portal, so
   *  term() and feature checks resolve to the concept being managed, not the host. */
  setPortalConfig: (config: { terminology: Terminology; features: Record<FeatureKey, boolean> } | null) => void;
  refetchConfig: () => void;
}

const DEFAULT_CONFIG: ConceptConfig = {
  concept: null,
  // The bundled LKQ wordmark is the platform default — shown during the pre-config
  // load (and if the config request is unavailable) so the LKQ login never flashes
  // the plain-text fallback. A concept's own logo replaces it once its config loads.
  branding: { ...FALLBACK_BRANDING, logoUrl: '/lkq-logo.svg', faviconUrl: null, source: 'platform' },
  terminology: {},
  features: resolveEffectiveFeatures({}),
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preview, setPreviewState] = useState<ResolvedBranding | null>(null);
  const [portalSkin, setPortalSkin] = useState<ResolvedBranding | null>(null);
  const [portalConfig, setPortalConfig] = useState<{ terminology: Terminology; features: Record<FeatureKey, boolean> } | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['concept-config', window.location.hostname],
    queryFn: async () => {
      const res = await api.get<ApiSuccess<ConceptConfig>>('/concept-config', {
        params: { host: window.location.hostname },
      });
      return res.data.data;
    },
    staleTime: 60_000,
  });

  // Seed the pre-fetch config with this host's cached branding (from a prior
  // visit) so the first paint is on-theme — no platform-default flash while
  // /concept-config loads. First-ever visit to a host has no cache and briefly
  // shows the default until the fetch resolves.
  const initialConfig = useMemo<ConceptConfig>(() => {
    const cached = readCachedBranding(window.location.hostname);
    return cached ? { ...DEFAULT_CONFIG, branding: cached } : DEFAULT_CONFIG;
  }, []);
  const config = data ?? initialConfig;

  // Persist the live host branding so the next visit to this host paints on-theme
  // immediately (the cache self-heals when a concept's branding changes).
  useEffect(() => {
    if (data?.branding) writeCachedBranding(window.location.hostname, data.branding);
  }, [data]);

  // useLayoutEffect (not useEffect): apply branding synchronously BEFORE the
  // browser paints, so a page never flashes the previous/platform theme before
  // re-skinning to the resolved concept (docs/09).
  useLayoutEffect(() => {
    applyBranding(preview ?? portalSkin ?? config.branding);
    if (config.concept && !preview && !portalSkin) document.title = `${config.concept.name} — Members Area`;
  }, [config, preview, portalSkin]);

  const setPreview = useCallback(
    (branding: Partial<Branding>, logoUrl?: string | null) => {
      const base = portalSkin ?? config.branding;
      setPreviewState({
        ...base,
        ...branding,
        logoUrl: logoUrl === undefined ? base.logoUrl : logoUrl,
      } as ResolvedBranding);
    },
    [config.branding, portalSkin],
  );

  const clearPreview = useCallback(() => setPreviewState(null), []);

  // In the Concept Management portal the active concept's terminology/features
  // (portalConfig) override the management host's own config; elsewhere it is null.
  const term = useCallback(
    (key: string, fallback: string) => (portalConfig?.terminology ?? config.terminology)[key] ?? fallback,
    [config.terminology, portalConfig],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      config, branding: preview ?? portalSkin ?? config.branding, isLoading, term,
      features: portalConfig?.features ?? config.features,
      setPreview, clearPreview, setPortalSkin, setPortalConfig,
      refetchConfig: () => void refetch(),
    }),
    [config, preview, portalSkin, portalConfig, isLoading, term, setPreview, clearPreview, refetch],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme outside ThemeProvider');
  return ctx;
}
